import { ChangeEvent, FormEvent, FunctionComponent, useEffect, useState } from 'react';
import { GoogleOAuthProvider, GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { AuthState, ErrorState, REGISTER_FAIL } from '../../reducers/types';
import GenericIcons from '../generics/GenericIcons';
import authImg from '../../img/vdr-red-auth.jpg';
import { loginGoogleAuth, register, RegisterData } from '../../actions/authActions';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { AppState } from '../../store';
import { clearErrors } from '../../actions/errorActions';
import Swal from 'sweetalert2';
import { Dropdown, DropdownItemProps } from 'semantic-ui-react';
import { SimpleObject } from '../../types';
import { getUncatchEndpointData } from '../../utils/request';
import '../../css/login.css';
import '../../css/signUp.css';

interface DropdownProps extends DropdownItemProps {
  value: string | number;
  text: string;
}

const clientId = '36950189098-epk7rnsviju6q9g16reoikeuvuk33to8.apps.googleusercontent.com';

const formInputs: Array<{
  name: string;
  id: keyof RegisterData;
  type: 'text' | 'password' | 'email' | 'reference';
  lowerCase?: boolean;
  validations?: Array<{
    description: string;
    validation: (value: string) => boolean;
  }>;
  autoFocus?: boolean;
  reference?: {
    name: string;
    endpoint: string;
    select: string;
    show: string;
  };
}> = [
  {
    name: 'Usuario',
    id: 'userUsername',
    type: 'text',
    lowerCase: true,
    autoFocus: true
  },
  {
    name: 'Contraseña',
    id: 'userPassword',
    type: 'password',
    validations: [
      { description: 'No puede contener espacios', validation: (value) => value.includes(' ') },
      {
        description: 'Debe incluir al menos una minúscula',
        validation: (value) => !/[a-z]/.test(value)
      },
      {
        description: 'Debe incluir al menos una mayúscula',
        validation: (value) => !/[A-Z]/.test(value)
      },
      {
        description: 'Debe incluir al menos un carácter especial',
        validation: (value) => !/[!@#$%^&*(),.?":{}|<>]/.test(value)
      },
      { description: 'Debe incluir al menos un número', validation: (value) => !/\d/.test(value) },
      {
        description: 'Debe tener 8 caracteres como mínimo',
        validation: (value) => value.length < 8
      },
      {
        description: 'No puede tener más de 20 caracteres',
        validation: (value) => value.length > 20
      }
    ]
  },
  {
    name: 'Nombre',
    id: 'userAccountFirstname',
    type: 'text'
  },
  {
    name: 'Apellido',
    id: 'userAccountLastname',
    type: 'text'
  },
  {
    name: 'Email',
    id: 'userAccountEmail',
    type: 'email'
  },
  {
    name: 'Empresa',
    id: 'userAccountCompanyName',
    type: 'text'
  },
  {
    name: '¿Qué cargo ocupas?',
    id: 'userOccupationId',
    type: 'reference',
    reference: {
      name: 'Ocupación',
      endpoint: 'userOccupation',
      select: '_id',
      show: 'userOccupationName'
    }
  }
];

type Props = {
  auth: AuthState;
  error: ErrorState;
  register: ({
    userUsername,
    userPassword,
    userAccountFirstname,
    userAccountLastname,
    userAccountEmail,
    userAccountCompanyName,
    userOccupationId
  }: RegisterData) => Promise<boolean>;
  loginGoogleAuth: (token: string) => Promise<void>;
  clearErrors: typeof clearErrors;
};

const SignUp: FunctionComponent<Props> = ({
  auth,
  error,
  register,
  loginGoogleAuth,
  clearErrors
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [referenceData, setReferenceData] = useState<{ [key: string]: Array<DropdownProps> }>({});
  const navigate = useNavigate();
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate('/');
    }
  }, [auth, navigate]);

  const [userInfo, setUserInfo] = useState<RegisterData>({
    userUsername: '',
    userPassword: '',
    userAccountFirstname: '',
    userAccountLastname: '',
    userAccountEmail: '',
    userAccountCompanyName: '',
    userOccupationId: ''
  });

  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (error) {
      // Check for register error
      if (error.id === REGISTER_FAIL) {
        setMsg(error.msg.msg);
      } else {
        setMsg(null);
      }
    }
  }, [error]);

  const [viewPassword, setViewPassword] = useState<boolean>(false);
  const handleViewPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setViewPassword(!viewPassword);
  };

  const handleOnSubmit = async (e: FormEvent<HTMLFormElement>) => {
    setIsLoading(true);
    e.preventDefault();
    for (const f of formInputs) {
      if ((userInfo[f.id as keyof RegisterData] || '').toString().trim() === '') {
        setMsg(`El campo '${f.name}' es obligatorio`);
        setIsLoading(false);
        return;
      } else {
        setMsg(null);
      }
    }

    const res = await register(userInfo);
    if (res) {
      Swal.fire({
        title: '¡Registro completado!',
        text: 'Recuerda que un administrador debe activar tu cuenta para poder utilizarla',
        icon: 'success'
      }).finally(() => navigate('/'));
    }
    setIsLoading(false);
  };

  const onSuccessGoogleAuth = async (credentialResponse: CredentialResponse) => {
    loginGoogleAuth(credentialResponse.credential!);
  };

  useEffect(() => {
    const filterReferences = formInputs.filter((f) => f.type === 'reference');
    filterReferences.forEach(async (f) => {
      if (f.reference) {
        const res = await getUncatchEndpointData({ endpoint: f.reference.endpoint });
        if (res && Array.isArray(res)) {
          setReferenceData((prev) => ({
            ...prev,
            [f.id]: res.map((row: SimpleObject, idx: number) => ({
              key: `${f.id}_${idx}`,
              value: row[f.reference?.select!],
              text: row[f.reference?.show!]
            }))
          }));
        }
      }
    });
  }, [formInputs]);

  return (
    <div className='sign-up-container'>
      <div className='vdr-auth-aside'>
        <img className='vdr-auth-aside-img' src={authImg} alt='' />
        <span className='vdr-caption vdr-auth-cap' aria-hidden='true'>
          Red de distribución · oriente de Santiago
        </span>
      </div>
      <div className='sign-up-content-container'>
        <span className='vdr-eyebrow vdr-eyebrow--bar vdr-auth-eyebrow' aria-hidden='true'>
          Plataforma VisiónDERRED
        </span>
        <h2 className='sign-up-title'>Crea una cuenta</h2>

        <GoogleOAuthProvider clientId={clientId!}>
          <GoogleLogin onSuccess={onSuccessGoogleAuth} />
        </GoogleOAuthProvider>

        <div className='sign-up-divide-line'>
          <p className='sign-up-divide-line-text'>o</p>
        </div>

        <p className='sign-up-description'>
          Necesitamos saber más sobre ti para ofrecerte una experiencia personalizada y optimizada
          en nuestro sitio web.
        </p>
        <form onSubmit={handleOnSubmit} className='sign-up-form-container'>
          {formInputs.map((btn, idx) => (
            <div key={`sign-up-btn-${idx}`} className='form-group'>
              <label className='form-group-label form-group-label-sign-up'>{btn.name} *</label>
              {btn.type === 'reference' && btn.reference ? (
                <Dropdown
                  className='sign-up-form-dropdown'
                  placeholder={`Seleccione ${btn.reference.name}`}
                  noResultsMessage='No hay coincidencias'
                  options={referenceData[btn.id]}
                  onChange={(_, data) => setUserInfo({ ...userInfo, [btn.id]: data.value })}
                  value={userInfo[btn.id]}
                  loading={isLoading}
                  disabled={isLoading}
                  selection
                  clearable
                  fluid
                  lazyLoad
                  search
                />
              ) : btn.type === 'password' ? (
                <>
                  <div className='sign-up-form-password-input'>
                    <input
                      value={userInfo[btn.id]}
                      className='form-group-input form-group-input-sign-up'
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const value = btn.lowerCase ? e.target.value.toLowerCase() : e.target.value;
                        setUserInfo({ ...userInfo, [e.target.name]: value });
                        clearErrors();
                      }}
                      type={viewPassword ? 'text' : 'password'}
                      placeholder='*********'
                      id={btn.id}
                      name={btn.id}
                      autoFocus={btn.autoFocus}
                      autoComplete='current-password'
                      required
                    />
                    <button
                      type='button'
                      className='toggle-icon'
                      aria-label={viewPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      onClick={handleViewPassword}>
                      <GenericIcons icon={viewPassword ? 'visibilityOffIcon' : 'visibilityIcon'} />
                    </button>
                  </div>
                  {userInfo[btn.id] && btn?.validations && (
                    <ul className='sign-up-form-password-validation-container'>
                      {btn.validations.map((v, i) =>
                        v.validation(userInfo[btn.id]) ? (
                          <li className='sign-up-form-password-validation' key={`v_${i}`}>
                            {v.description}
                          </li>
                        ) : null
                      )}
                    </ul>
                  )}
                </>
              ) : (
                <input
                  value={userInfo[btn.id]}
                  className='form-group-input form-group-input-sign-up'
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const value = btn.lowerCase ? e.target.value.toLowerCase() : e.target.value;
                    setUserInfo({ ...userInfo, [e.target.name]: value });
                    clearErrors();
                  }}
                  type={btn.type}
                  id={btn.id}
                  name={btn.id}
                  autoFocus={btn.autoFocus}
                  required
                />
              )}
            </div>
          ))}

          {msg && (
            <div className='sign-up-form-message-container'>
              <GenericIcons icon='errorIcon' imageStyle={{ height: '100%' }} />
              {msg}
            </div>
          )}

          <div className='sign-up-button-container'>
            <button
              type='submit'
              className='primary-button-style sign-up-button'
              disabled={isLoading}>
              Crear cuenta
              <span className='vdr-auth-arrow' aria-hidden='true'>
                →
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default connect(({ auth, error }: AppState) => ({ auth, error }), {
  register,
  loginGoogleAuth,
  clearErrors
})(SignUp);
